'use client';
import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { CheckSVG, CloseSVG, SendSVG, WhatsappSVG } from './Icons';
import { reducer } from './reducer';
import './styles.css';

export interface FloatingWhatsAppProps {
  /** Enable floating button */
  enable?: boolean;
  /** Callback function fires on click */
  onClick?: (_event: React.MouseEvent<HTMLButtonElement>) => void;
  /** Callback function fires on submit with event and form input value passed */
  onSubmit?: (_event: React.FormEvent<HTMLFormElement>, _formValue: string) => void;
  /** Callback function fires on close */
  onClose?: () => void;
  /** Callback function fired when notification runs */
  onNotification?: () => void;
  /** Callback function called when notification loop done */
  onLoopDone?: () => void;

  /** Phone number in [intenational format](https://faq.whatsapp.com/general/contacts/how-to-add-an-international-phone-number) */
  phoneNumber?: string;
  /** Account Name */
  accountName?: string;
  /** Set chat box height */
  chatboxHeight?: number;
  /** Inline style applied to chat box */
  chatboxStyle?: React.CSSProperties;
  /** CSS className applied to chat box */
  chatboxClassName?: string;
  /** Change user avatar using [static assets](https://create-react-app.dev/docs/adding-images-fonts-and-files/) */
  avatar?: string;
  /** Text below the account username */
  statusMessage?: string;
  /** Text inside the chat box */
  chatMessage?: string;
  /** Input placeholder */
  placeholder?: string;

  /** Time delay after which the chatMessage is displayed (in seconds) */
  messageDelay?: number;

  /** Allow notifications (Disabled after user opens the chat box) */
  notification?: boolean;
  /** Time delay between notifications in seconds */
  notificationDelay?: number;
  /** Repeat notifications loop */
  notificationLoop?: number;
  /** Inline style applied to notification */
  notificationStyle?: React.CSSProperties;
  /** CSS className applied to notification */
  notificationClassName?: string;

  /** Closes the chat box if click outside the chat box */
  allowClickAway?: boolean;
  /** Closes the chat box if `Escape` key is clicked */
  allowEsc?: boolean;
  /** Enable / Disable dark mode */
  darkMode?: boolean;
  /** Inline style  applied to the main wrapping `Div` */
  style?: React.CSSProperties;
  /** CSS className applied to the main wrapping `Div` */
  className?: string;

  /** Inline style applied to button */
  buttonStyle?: React.CSSProperties;
  /** CSS className applied to button */
  buttonClassName?: string;

  /** Title text displayed next to the WhatsApp icon on the button */
  buttonTitle?: string;
  /** Subtitle text displayed next to the WhatsApp icon on the button */
  buttonSubtitle?: string;
}

export function FloatingWhatsApp({
  enable,
  onClick,
  onSubmit,
  onClose,
  onNotification,
  onLoopDone,

  phoneNumber = '5491131878888',
  accountName = 'Directo',
  avatar = '/assets/icon-512.png',
  statusMessage = 'En línea',
  chatMessage = '¡Hola!🤝 \n¿Cómo puedo ayudarte?',
  placeholder = 'Escribí un mensaje',

  messageDelay = 2,

  allowClickAway = false,
  allowEsc = false,

  notification = true,
  notificationDelay = 60,
  notificationLoop = 0,
  notificationStyle,
  notificationClassName = 'floating-whatsapp-notification',

  buttonStyle,
  buttonClassName = 'floating-whatsapp-button',

  buttonTitle,
  buttonSubtitle,

  chatboxHeight = 320,
  chatboxStyle,
  chatboxClassName = 'floating-whatsapp-chatbox',

  darkMode = false,
  style,
  className = 'floating-whatsapp',
}: FloatingWhatsAppProps) {
  const [{ isOpen, isDelay, isNotification }, dispatch] = useReducer(reducer, {
    isOpen: false,
    isDelay: true,
    isNotification: false,
  });

  const timeNow = useMemo(() => new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }), []);

  const inputRef = useRef<HTMLInputElement | null>(null);
  const chatboxRef = useRef<HTMLDivElement | null>(null);
  const loops = useRef(0);
  const notificationInterval = useRef(0);
  const isClient = typeof window !== 'undefined';

  const [isMobile, setIsMobile] = useState<boolean>(isClient && (window?.innerWidth || 0) < 768);

  function handleWindowSizeChange() {
    setIsMobile(isClient && (window?.innerWidth || 0) < 768);
  }
  useEffect(() => {
    window.addEventListener('resize', handleWindowSizeChange);
    return () => {
      window.removeEventListener('resize', handleWindowSizeChange);
    };
  }, []);

  const handleNotification = useCallback(() => {
    if (!notification) return;

    dispatch({ type: 'notification' });
    if (onNotification) onNotification();
    if (notificationLoop > 0) {
      loops.current += 1;

      if (loops.current === notificationLoop) {
        clearInterval(notificationInterval.current);
        if (onLoopDone) onLoopDone();
      }
    }
  }, [notification, notificationLoop, onNotification, onLoopDone]);

  useEffect(() => {
    const delayInSecond = notificationDelay * 1000;
    if (delayInSecond < 10) return console.error('notificationDelay prop value must be at least 10 seconds.');

    notificationInterval.current = window.setInterval(handleNotification, delayInSecond);

    return () => clearInterval(notificationInterval.current);
  }, [handleNotification, notificationDelay]);

  const handleOpen = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();

      if (isOpen) return;

      clearInterval(notificationInterval.current);
      dispatch({ type: 'open' });
      setTimeout(() => dispatch({ type: 'delay' }), messageDelay * 1000);
      if (onClick) onClick(event);
    },
    [isOpen, onClick, messageDelay],
  );

  const handleClose = useCallback(() => {
    dispatch({ type: 'close' });

    if (onClose) onClose();
  }, [onClose]);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!inputRef.current?.value) return;

    window.open(
      `https://${isMobile ? 'api' : 'web'}.whatsapp.com/send/?phone=${phoneNumber}&text=${inputRef.current.value}`,
    );
    if (onSubmit) onSubmit(event, inputRef.current.value);
    inputRef.current.value = '';
  };

  useEffect(() => {
    const onClickOutside = (event: MouseEvent) => {
      if (!allowClickAway || !isOpen) return;
      if (chatboxRef.current?.contains(event.target as Node)) return;

      handleClose();
    };
    document.addEventListener('click', onClickOutside, false);

    return () => document.removeEventListener('click', onClickOutside);
  }, [allowClickAway, isOpen, handleClose]);

  useEffect(() => {
    const onEscKey = (event: KeyboardEvent) => {
      if (!allowEsc || !isOpen) return;

      if (event.key === 'Escape') handleClose();
    };

    document.addEventListener('keydown', onEscKey, false);

    return () => document.removeEventListener('keydown', onEscKey);
  }, [allowEsc, isOpen, handleClose]);
  const [display, setDisplay] = useState('none');
  useEffect(() => {
    setDisplay(enable ? 'block' : 'none');
  }, [enable]);

  return (
    <div className={`floatingWhatsapp ${darkMode ? `dark ` : ''} ${className}`} style={{ display, ...style }}>
      <button
        type="button"
        className={`whatsappButton ${buttonClassName}`}
        onClick={handleOpen}
        style={buttonStyle}
        aria-label={buttonTitle ? undefined : 'Abrir chat de WhatsApp'}
        aria-expanded={isOpen}
      >
        <WhatsappSVG />

        {(buttonTitle || buttonSubtitle) && (
          <div className="whatsappButtonText">
            {buttonTitle && <span className="whatsappButtonTitle">{buttonTitle}</span>}
            {buttonSubtitle && <span className="whatsappButtonSubtitle">{buttonSubtitle}</span>}
          </div>
        )}

        {isNotification && (
          <span className={`notificationIndicator ${notificationClassName}`} style={notificationStyle}>
            1
          </span>
        )}
      </button>
      <div
        className={`whatsappChatBox ${isOpen ? 'open' : 'close'} ${chatboxClassName}`}
        ref={chatboxRef}
        aria-hidden={!isOpen}
        inert={!isOpen}
        style={{ height: isOpen ? chatboxHeight : 0, ...chatboxStyle }}
      >
        <header className={'chatHeader'}>
          <div className={'avatar'}>
            <img src={avatar} width="60" height="60" alt="whatsapp-avatar" />
          </div>
          <div className={'status'}>
            <span className={'statusTitle'}>{accountName}</span>
            <span className={'statusSubtitle'}>{statusMessage}</span>
          </div>
          <button type="button" className={'close'} onClick={handleClose} aria-label="Cerrar chat">
            <CloseSVG />
          </button>
        </header>

        <div className={'chatBody'}>
          {isDelay ? (
            <div className={'chatBubble'}>
              <div className={'typing'}>
                <div className={'dot'} />
                <div className={'dot'} />
                <div className={'dot'} />
              </div>
            </div>
          ) : (
            <div className={'message'}>
              <span className={'triangle'} />
              <span className={'accountName'}>{accountName}</span>
              <p className={'messageBody'}>{chatMessage}</p>
              <span className={'messageTime'}>
                {timeNow}
                <span style={{ marginLeft: 5 }}>
                  <CheckSVG />
                </span>
              </span>
            </div>
          )}
        </div>

        <footer className={'chatFooter'}>
          <form onSubmit={handleSubmit}>
            <input
              className={'input'}
              placeholder={placeholder}
              ref={inputRef}
              aria-label="Mensaje"
              defaultValue={'Hola, quiero más info.'}
              dir="auto"
            />
            <button type="submit" className={'buttonSend'} aria-label="Enviar mensaje">
              <SendSVG />
            </button>
          </form>
        </footer>
      </div>
    </div>
  );
}
